import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { mandatory } from "../../../utils/Functions";
import { VaccinationCategory } from "../enums/VaccinationCategory";

const RabiesNameField = ({ isDisabled }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="rabiesName"
      control={control}
      defaultValue=""
      rules={{
        required: "EmptyField",
        maxLength: { value: 120, message: "MaxCharacters" },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          size="small"
          label={mandatory(t("Name"))}
          fullWidth
          error={!!errors.rabiesName}
          helperText={errors.rabiesName ? t(errors.rabiesName.message) : ""}
          inputProps={{ maxLength: 120 }}
          disabled={isDisabled}
          data-testid="appointment.finish-meeting.vaccination.rabies.name-input"
        />
      )}
    />
  );
};
const RabiesDateOfApplicationField = ({ isDisabled }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="rabiesDateOfApplication"
      control={control}
      rules={{ required: "EmptyField" }}
      render={({ field: { onChange, value } }) => (
        <FormControl fullWidth>
          <MobileDatePicker
            value={value ?? null}
            format="DD/MM/YYYY"
            label={mandatory(t("DateOfAppointment"))}
            onChange={onChange}
            required
            disabled={isDisabled}
            slotProps={{
              textField: {
                size: "small",
                error: !!errors.rabiesDateOfApplication,
                helperText: errors.rabiesDateOfApplication
                  ? t(errors.rabiesDateOfApplication.message)
                  : "",
                inputProps: {
                  "data-testid":
                    "appointment.finish-meeting.vaccination.rabies.date-of-application-input",
                },
              },
              previousIconButton: {
                "data-testid":
                  "appointment.finish-meeting.vaccination.rabies.date-of-application-input.prev-month",
              },
              nextIconButton: {
                "data-testid":
                  "appointment.finish-meeting.vaccination.rabies.date-of-application-input.next-month",
              },
              day: (ownerState) => ({
                "data-testid": `appointment.finish-meeting.vaccination.rabies.date-of-application-input.day-${ownerState.day.format("YYYY-MM-DD")}`,
              }),
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
      )}
    />
  );
};

const RabiesSerialNumberField = ({ isDisabled }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="rabiesSerialNumber"
      control={control}
      defaultValue=""
      rules={{
        required: "EmptyField",
        maxLength: { value: 120, message: "MaxCharacters" },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          size="small"
          label={mandatory(t("SerialNumber"))}
          fullWidth
          error={!!errors.rabiesSerialNumber}
          helperText={
            errors.rabiesSerialNumber
              ? t(errors.rabiesSerialNumber.message)
              : ""
          }
          inputProps={{ maxLength: 120 }}
          disabled={isDisabled}
          data-testid="appointment.finish-meeting.vaccination.rabies.serial-number-input"
        />
      )}
    />
  );
};
const IsFirstRabiesVaccineField = ({ isDisabled }) => {
  const { control } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="isFirstRabiesVaccine"
      control={control}
      defaultValue={false}
      render={({ field: { value, onChange, ...field } }) => (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "grey.500",
            borderRadius: "4px",
            padding: "8.5px 14px",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            "&:hover": {
              borderColor: "grey.700",
            },
            "&:focus-within": {
              borderColor: "primary.main",
              borderWidth: "2px",
            },
          }}
          onClick={() => !isDisabled && onChange(!value)}
        >
          <Typography
            variant="body1"
            sx={{
              mr: 1,
              color: isDisabled ? "text.disabled" : "text.secondary",
            }}
          >
            {t("IsFirstRabiesVaccine")}
          </Typography>
          <Checkbox
            {...field}
            checked={!!value}
            onChange={(e) => {
              e.stopPropagation();
              onChange(e.target.checked);
            }}
            disabled={isDisabled}
            sx={{ p: 0 }}
            data-testid="appointment.finish-meeting.vaccination.rabies.is-first-checkbox"
          />
        </Box>
      )}
    />
  );
};
const ImmunityPeriodField = ({ isDisabled }) => {
  const { control } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="immunityPeriod"
      control={control}
      defaultValue=""
      render={({ field }) => (
        <TextField
          {...field}
          select
          size="small"
          label={t("ImmunityPeriod")}
          fullWidth
          disabled={isDisabled}
          data-testid="appointment.finish-meeting.vaccination.rabies.immunity-period-select"
        >
          <MenuItem value="1">1 {t("SingleYear")}</MenuItem>
          <MenuItem value="2">2 {t("PluralYear")}</MenuItem>
          <MenuItem value="3">3 {t("PluralYear")}</MenuItem>
        </TextField>
      )}
    />
  );
};

const ImmunityFromDateField = ({ isDisabled, maxDate }) => {
  const { control } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="immunityFromDate"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormControl fullWidth>
          <MobileDatePicker
            value={value ?? null}
            format="DD/MM/YYYY"
            label={t("ImmunityFrom")}
            onChange={onChange}
            disabled={isDisabled}
            maxDate={maxDate ? dayjs(maxDate) : undefined}
            slotProps={{
              textField: {
                size: "small",
                inputProps: {
                  "data-testid":
                    "appointment.finish-meeting.vaccination.rabies.immunity-from-input",
                },
              },
              actionBar: {
                actions: ["clear", "cancel", "accept"],
              },
              previousIconButton: {
                "data-testid":
                  "appointment.finish-meeting.vaccination.rabies.immunity-from-input.prev-month",
              },
              nextIconButton: {
                "data-testid":
                  "appointment.finish-meeting.vaccination.rabies.immunity-from-input.next-month",
              },
              day: (ownerState) => ({
                "data-testid": `appointment.finish-meeting.vaccination.rabies.immunity-from-input.day-${ownerState.day.format("YYYY-MM-DD")}`,
              }),
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
      )}
    />
  );
};

const ImmunityToDateField = ({ isDisabled,minDate }) => {
  const { control } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="immunityToDate"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormControl fullWidth>
          <MobileDatePicker
            value={value ?? null}
            format="DD/MM/YYYY"
            label={t("ImmunityTo")}
            onChange={onChange}
            disabled={isDisabled}
            minDate={minDate ? dayjs(minDate) : undefined}
            slotProps={{
              textField: {
                size: "small",
                inputProps: {
                  "data-testid":
                    "appointment.finish-meeting.vaccination.rabies.immunity-to-input",
                },
              },
              actionBar: {
                actions: ["clear", "cancel", "accept"],
              },
              previousIconButton: {
                "data-testid":
                  "appointment.finish-meeting.vaccination.rabies.immunity-to-input.prev-month",
              },
              nextIconButton: {
                "data-testid":
                  "appointment.finish-meeting.vaccination.rabies.immunity-to-input.next-month",
              },
              day: (ownerState) => ({
                "data-testid": `appointment.finish-meeting.vaccination.rabies.immunity-to-input.day-${ownerState.day.format("YYYY-MM-DD")}`,
              }),
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
      )}
    />
  );
};

const RabiesExpirationDateField = ({ isDisabled }) => {
  const { control } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="rabiesExpirationDate"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormControl fullWidth>
          <MobileDatePicker
            value={value ?? null}
            format="DD/MM/YYYY"
            label={t("VaccineExpirationDate")}
            onChange={onChange}
            disabled={isDisabled}
            slotProps={{
              textField: {
                size: "small",
                inputProps: {
                  "data-testid":
                    "appointment.finish-meeting.vaccination.rabies.expiration-date-input",
                },
              },
              previousIconButton: {
                "data-testid":
                  "appointment.finish-meeting.vaccination.rabies.expiration-date-input.prev-month",
              },
              nextIconButton: {
                "data-testid":
                  "appointment.finish-meeting.vaccination.rabies.expiration-date-input.next-month",
              },
              day: (ownerState) => ({
                "data-testid": `appointment.finish-meeting.vaccination.rabies.expiration-date-input.day-${ownerState.day.format("YYYY-MM-DD")}`,
              }),
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
      )}
    />
  );
};
const RabiesDescriptionField = ({ isDisabled }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="rabiesDescription"
      control={control}
      defaultValue=""
      rules={{
        maxLength: { value: 250, message: "MaxCharacters" },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          size="small"
          label={t("VaccineDescription")}
          fullWidth
          multiline
          rows={3}
          disabled={isDisabled}
          error={!!errors.rabiesDescription}
          helperText={
            errors.rabiesDescription ? t(errors.rabiesDescription.message) : ""
          }
          inputProps={{ maxLength: 250 }}
          data-testid="appointment.finish-meeting.vaccination.rabies.description-input"
        />
      )}
    />
  );
};

export default function AddRabiesVaccinationTab({ append, fields }) {
  const { handleSubmit, setValue,watch } = useFormContext();

  const hasRabiesVaccination = fields.some(
    (v) => v.vaccinationCategory === VaccinationCategory.RABIES
  );

  const { t } = useTranslation();
  const immunityFromDate = watch("immunityFromDate");
  const immunityToDate = watch("immunityToDate");

  const formatDateOrNull = (dateValue) => {
    if (!dateValue) return null;
    if (dayjs.isDayjs(dateValue)) {
      return dateValue.format("DD/MM/YYYY");
    }
    return null;
  };

  function resetValues() {
    const resetStringFields = [
      "rabiesName",
      "rabiesSerialNumber",
      "rabiesDescription",
      "immunityPeriod",
    ];
    resetStringFields.forEach((field) => setValue(field, ""));

    const resetDateFields = [
      "rabiesDateOfApplication",
      "rabiesExpirationDate",
      "immunityFromDate",
      "immunityToDate",
    ];
    resetDateFields.forEach((field) => setValue(field, null));

    setValue("isFirstRabiesVaccine", false);
  }

  const handleAddRabies = (data) => {
    const vaccination = {
      vaccinationCategory: VaccinationCategory.RABIES,
      name: data.rabiesName,
      serialNumber: data.rabiesSerialNumber,
      dateOfApplication: formatDateOrNull(data.rabiesDateOfApplication),
      expirationDate: formatDateOrNull(data.rabiesExpirationDate),
      isFirstRabiesVaccine: data.isFirstRabiesVaccine || false,
      immunityPeriod: data.immunityPeriod
        ? parseInt(data.immunityPeriod)
        : null,
      immunityFromDate: formatDateOrNull(data.immunityFromDate),
      immunityToDate: formatDateOrNull(data.immunityToDate),
      description: data.rabiesDescription || null,
    };
    append(vaccination);

    resetValues();
  };

  return (
    <form onSubmit={handleSubmit(handleAddRabies)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <RabiesNameField isDisabled={hasRabiesVaccination} />
        </Grid>
        <Grid item xs={12}>
          <RabiesDateOfApplicationField isDisabled={hasRabiesVaccination} />
        </Grid>
        <Grid item xs={12}>
          <RabiesSerialNumberField isDisabled={hasRabiesVaccination} />
        </Grid>
        <Grid item xs={12}>
          <IsFirstRabiesVaccineField isDisabled={hasRabiesVaccination} />
        </Grid>
        <Grid item xs={12}>
          <ImmunityPeriodField isDisabled={hasRabiesVaccination} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ImmunityFromDateField isDisabled={hasRabiesVaccination} maxDate={immunityToDate} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ImmunityToDateField isDisabled={hasRabiesVaccination} minDate={immunityFromDate} />
        </Grid>
        <Grid item xs={12}>
          <RabiesExpirationDateField isDisabled={hasRabiesVaccination} />
        </Grid>
        <Grid item xs={12}>
          <RabiesDescriptionField isDisabled={hasRabiesVaccination} />
        </Grid>

        <Grid item xs={12} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            type="submit"
            sx={{ mb: 2 }}
            disabled={hasRabiesVaccination}
            data-testid="appointment.finish-meeting.vaccination.rabies.add-button"
          >
            {t("Add")}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
